<script setup>
// Tela propria de uma landing page, em 3 abas — Resultados vem PRIMEIRO
// (a LP existe pra converter; os numeros vem antes do codigo):
//   Resultados — KPIs, acessos×conversoes por dia, origem×meio, conversoes
//                por link de campanha e ultimos leads (clique abre o contato)
//   Conteudo   — codigo + previa ao vivo em MOLDURA DE DISPOSITIVO (frame na
//                proporcao real 1280x800 / 390x844, reduzido por escala)
//   Links      — links de campanha (UTMs) salvos DESTA pagina
// Salvar nunca publica: o que esta no ar so muda pelo botao Publicar.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHoverTip } from 'dashboard/composables/useHoverTip';
import { useRoute, useRouter } from 'vue-router';
import { toast } from 'dashboard/composables/useToast';
import { useAccount } from 'dashboard/composables/useAccount';
import LandingPagesAPI from 'dashboard/api/landingPages';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsSectionCard from 'dashboard/components-next/Settings/SettingsSectionCard.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CrmKpiCard from 'dashboard/routes/dashboard/crm/components/reports/CrmKpiCard.vue';
import {
  LANDING_PAGE_TRAFFIC_TYPES,
  normalizeDimension,
  buildTrackedUrl,
} from 'dashboard/helper/utmVocabulary';
import { useLandingPageSite } from './composables/useLandingPageSite';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const {
  site,
  isLoading: isLoadingSite,
  apiError,
  fetchSite,
} = useLandingPageSite();

const pageId = computed(() => route.params.pageId || null);
const isNew = computed(() => !pageId.value);

const TABS = ['results', 'content', 'links', 'advanced'];
const { tip, showTip, hideTip, tipStyle } = useHoverTip();

// Pagina existente nasce em Resultados (sem piscar em Conteudo); nova
// nasce em Conteudo — o pageId ja esta na rota no setup.
const initialTab = () => {
  if (TABS.includes(route.query.tab)) return route.query.tab;
  return route.params.pageId ? 'results' : 'content';
};
const activeTab = ref(initialTab());
const isLoadingPage = ref(false);
const isSaving = ref(false);
const pageMeta = ref(null);
const form = ref({
  title: '',
  slug: '',
  html: '',
  conversion_event: '',
  campaign_links: [],
  form_events: [],
  form_events_enabled: false,
});

// ── Previa ao vivo: moldura na PROPORCAO do dispositivo ──
// O html e renderizado na largura real (1280 ou 390) e o frame inteiro e
// reduzido por escala ate caber no palco — o layout que aparece e o que o
// dispositivo veria, so que em miniatura.
const DEVICES = {
  desktop: { width: 1280, height: 800 },
  mobile: { width: 390, height: 844 },
};
const STAGE_PADDING = 24;
const previewDevice = ref('desktop');
const previewHtml = ref('');
const previewStage = ref(null);
const stageSize = ref({ width: 0, height: 0 });
let stageObserver = null;

// O palco so existe depois do carregamento (v-if) — observa quando aparece.
watch(previewStage, element => {
  stageObserver?.disconnect();
  if (!element) return;
  stageObserver = new ResizeObserver(entries => {
    const box = entries[0].contentRect;
    stageSize.value = { width: box.width, height: box.height };
  });
  stageObserver.observe(element);
});
onUnmounted(() => stageObserver?.disconnect());

const device = computed(() => DEVICES[previewDevice.value]);
const previewScale = computed(() => {
  const { width, height } = stageSize.value;
  if (!width || !height) return 0.4;
  return Math.min(
    1,
    (width - STAGE_PADDING) / device.value.width,
    (height - STAGE_PADDING) / device.value.height
  );
});
const screenStyle = computed(() => ({
  width: `${Math.round(device.value.width * previewScale.value)}px`,
  height: `${Math.round(device.value.height * previewScale.value)}px`,
}));
const frameStyle = computed(() => ({
  width: `${device.value.width}px`,
  height: `${device.value.height}px`,
  transform: `scale(${previewScale.value})`,
  transformOrigin: 'top left',
}));

let previewTimer = null;
watch(
  () => form.value.html,
  html => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(() => {
      previewHtml.value = html;
    }, 500);
  }
);

// ── Resultados (30d): carregado quando a pagina existe ──
const EMPTY_INSIGHTS = {
  kpis: {},
  series: [],
  sources: [],
  access_origins: [],
  links: [],
  recent: [],
};
const insights = ref(EMPTY_INSIGHTS);
const insightsPeriod = ref(30);
const isLoadingInsights = ref(false);

const loadInsights = async () => {
  if (isNew.value) return;
  isLoadingInsights.value = true;
  try {
    const { data } = await LandingPagesAPI.pageInsights(
      site.value.id,
      pageId.value,
      insightsPeriod.value
    );
    insights.value = data;
  } catch {
    insights.value = EMPTY_INSIGHTS;
  } finally {
    isLoadingInsights.value = false;
  }
};

const kpis = computed(() => insights.value?.kpis || {});
const fmtHour = hour => `${String(hour).padStart(2, '0')}h`;
const kpiCards = computed(() => [
  {
    key: 'conversions',
    value: kpis.value.conversions ?? 0,
    subtitle:
      kpis.value.trend != null
        ? t(
            kpis.value.trend >= 0
              ? 'LANDING_PAGES.RESULTS.TREND_UP'
              : 'LANDING_PAGES.RESULTS.TREND_DOWN',
            { pct: Math.abs(kpis.value.trend) }
          )
        : '',
    category: 'highlight',
  },
  {
    key: 'views',
    value: kpis.value.views ?? 0,
    subtitle: t('LANDING_PAGES.RESULTS.VIEWS_SUB', {
      n: Math.round((kpis.value.views ?? 0) / insightsPeriod.value),
    }),
    category: 'volume',
  },
  {
    key: 'rate',
    value: kpis.value.rate != null ? `${kpis.value.rate}%` : '—',
    subtitle: t('LANDING_PAGES.RESULTS.RATE_SUB'),
    category: 'revenue',
  },
  {
    key: 'best_source',
    value: kpis.value.best_source?.source || '—',
    subtitle: kpis.value.best_source
      ? t('LANDING_PAGES.RESULTS.BEST_SOURCE_SUB', {
          count: kpis.value.best_source.count,
          share: kpis.value.best_source.share,
        })
      : '',
    category: 'time',
  },
  {
    key: 'best_hours',
    value: kpis.value.best_hours
      ? `${fmtHour(kpis.value.best_hours.from)}–${fmtHour(kpis.value.best_hours.to)}`
      : '—',
    subtitle: kpis.value.best_hours
      ? t('LANDING_PAGES.RESULTS.BEST_HOURS_SUB', {
          share: kpis.value.best_hours.share,
        })
      : '',
    category: 'time',
  },
]);
const series = computed(() => insights.value?.series || []);
const maxViews = computed(() =>
  Math.max(...series.value.map(day => day.views), 1)
);
const maxConversions = computed(() =>
  Math.max(...series.value.map(day => day.conversions), 1)
);
// Linha de conversoes sobre as barras de acesso (escalas proprias, senao a
// linha some — acessos sao ~10-50x maiores).
const CHART_W = 600;
const CHART_H = 110;
const conversionPoints = computed(() => {
  const n = series.value.length || 1;
  const bw = CHART_W / n;
  return series.value
    .map(
      (day, i) =>
        `${((i + 0.5) * bw).toFixed(1)},${(
          CHART_H -
          (day.conversions / maxConversions.value) * (CHART_H - 14)
        ).toFixed(1)}`
    )
    .join(' ');
});
const linkConversions = computed(() => {
  const map = {};
  (insights.value?.links || []).forEach(link => {
    map[link.name] = link.conversions;
  });
  return map;
});
const fmtDay = iso => {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
};
// "ha 22 min" / "ha 3 h" no mesmo dia; dd/MM HH:mm dali pra tras.
const fmtDateTime = ts => {
  const diffMin = Math.floor((Date.now() / 1000 - ts) / 60);
  if (diffMin >= 0 && diffMin < 60)
    return t('LANDING_PAGES.RESULTS.TIME_AGO_MIN', {
      n: Math.max(diffMin, 1),
    });
  if (diffMin >= 0 && diffMin < 24 * 60)
    return t('LANDING_PAGES.RESULTS.TIME_AGO_H', {
      n: Math.floor(diffMin / 60),
    });
  const d = new Date(ts * 1000);
  return `${String(d.getDate()).padStart(2, '0')}/${String(
    d.getMonth() + 1
  ).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
};
const goToContact = lead => {
  if (!lead.contact_id) return;
  router.push(
    accountScopedRoute('contact_profile_dashboard', {
      contactId: lead.contact_id,
    })
  );
};

// ── Links de campanha ──
const linkForm = ref({
  name: '',
  type: LANDING_PAGE_TRAFFIC_TYPES[0].key,
  utm_campaign: '',
  utm_content: '',
  custom_source: '',
  custom_medium: '',
});
const activeType = computed(
  () =>
    LANDING_PAGE_TRAFFIC_TYPES.find(x => x.key === linkForm.value.type) ||
    LANDING_PAGE_TRAFFIC_TYPES[0]
);
const isCustomType = computed(() => activeType.value.custom === true);
const linkDimensions = computed(() => ({
  utm_source: isCustomType.value
    ? linkForm.value.custom_source
    : activeType.value.source,
  utm_medium: isCustomType.value
    ? linkForm.value.custom_medium
    : activeType.value.medium,
  utm_campaign: linkForm.value.utm_campaign,
  utm_content: linkForm.value.utm_content,
}));
const publicUrl = computed(
  () =>
    pageMeta.value?.public_url ||
    (site.value && form.value.slug
      ? `${site.value.url}${form.value.slug === 'index' ? '/' : `/${form.value.slug}/`}`
      : '')
);
const linkPreview = computed(() =>
  buildTrackedUrl(publicUrl.value, linkDimensions.value)
);
const canAddLink = computed(
  () =>
    linkForm.value.name.trim() &&
    normalizeDimension(linkDimensions.value.utm_source)
);

const addLink = () => {
  if (!canAddLink.value) return;
  form.value.campaign_links = [
    ...form.value.campaign_links,
    {
      name: linkForm.value.name.trim(),
      url: linkPreview.value,
      utm_source: normalizeDimension(linkDimensions.value.utm_source),
      utm_medium: normalizeDimension(linkDimensions.value.utm_medium),
      utm_campaign: normalizeDimension(linkForm.value.utm_campaign),
      utm_content: normalizeDimension(linkForm.value.utm_content),
      created_at: new Date().toISOString(),
    },
  ];
  linkForm.value = {
    name: '',
    type: LANDING_PAGE_TRAFFIC_TYPES[0].key,
    utm_campaign: '',
    utm_content: '',
    custom_source: '',
    custom_medium: '',
  };
};

const linkHeaders = computed(() => [
  t('LANDING_PAGES.UTM.COL_LINK'),
  t('LANDING_PAGES.UTM.COL_SOURCE_MEDIUM'),
  t('LANDING_PAGES.UTM.COL_CAMPAIGN'),
  { label: t('LANDING_PAGES.UTM.COL_CONVERSIONS'), align: 'right' },
  '',
]);

const removeLink = index => {
  form.value.campaign_links = form.value.campaign_links.filter(
    (_, i) => i !== index
  );
};

const copyLink = async url => {
  try {
    await navigator.clipboard.writeText(url);
    toast.success(t('LANDING_PAGES.UTM.COPIED'));
  } catch {
    toast.error(t('LANDING_PAGES.UTM.COPY_FAILED'));
  }
};

// Chip do cabecalho: mesmo vocabulario da lista de paginas.
const headerBadge = computed(() => {
  const meta = pageMeta.value || {};
  if (meta.unpublished_changes && meta.status === 'published')
    return {
      label: t('LANDING_PAGES.PAGES.UNPUBLISHED_CHANGES'),
      cls: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
  const map = {
    published: {
      key: 'STATUS_PUBLISHED',
      cls: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    },
    publishing: {
      key: 'STATUS_PUBLISHING',
      cls: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    },
    publish_failed: {
      key: 'STATUS_FAILED',
      cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    },
  };
  const entry = map[meta.status] || {
    key: 'STATUS_DRAFT',
    cls: 'bg-n-slate-3 text-n-slate-11',
  };
  return { label: t(`LANDING_PAGES.PAGES.${entry.key}`), cls: entry.cls };
});

// ── Carregar / salvar / publicar ──
const backRoute = computed(() => accountScopedRoute('settings_landing_pages'));
const backToList = () => router.push(backRoute.value);

// ── Avancado: eventos por formulario ────────────────────────────────────
// Detecta os <form> do RASCUNHO atual (DOMParser — pega forms de popup
// tambem) e deixa configurar, por form: evento no CRM, origem e evento
// padrao da Meta. O casamento e por id (robusto) ou pela ordem na pagina.
const META_STANDARD_EVENTS = [
  'Lead',
  'CompleteRegistration',
  'Contact',
  'SubmitApplication',
  'Schedule',
  'StartTrial',
  'Subscribe',
  'Purchase',
  'AddToCart',
  'InitiateCheckout',
  'ViewContent',
];
const META_CUSTOM_OPTION = '__custom__';

const detectedForms = computed(() => {
  try {
    const doc = new DOMParser().parseFromString(
      form.value.html || '',
      'text/html'
    );
    return Array.from(doc.querySelectorAll('form'))
      .slice(0, 20)
      .map((el, index) => {
        const btn = el.querySelector('button, input[type=submit]');
        const fields = Array.from(
          el.querySelectorAll(
            'input[name], input[placeholder], select[name], textarea[name]'
          )
        )
          .slice(0, 4)
          .map(f => f.getAttribute('name') || f.getAttribute('placeholder'))
          .filter(Boolean);
        return {
          index,
          form_id: el.getAttribute('id') || null,
          button: (btn?.textContent || btn?.value || '').trim().slice(0, 60),
          fields,
        };
      });
  } catch {
    return [];
  }
});

const configFor = detected =>
  form.value.form_events.find(cfg =>
    cfg.match_by === 'id' && cfg.form_id
      ? cfg.form_id === detected.form_id
      : Number(cfg.index) === detected.index
  );

const setFormConfig = (detected, key, value) => {
  let cfg = configFor(detected);
  if (!cfg) {
    cfg = {
      match_by: 'index',
      form_id: detected.form_id,
      index: detected.index,
      crm_event: '',
      lead_source: '',
      meta_event: '',
    };
    form.value.form_events.push(cfg);
  }
  cfg[key] = value;
  cfg.form_id = detected.form_id;
  cfg.index = detected.index;
  if (!cfg.crm_event && !cfg.lead_source && !cfg.meta_event) {
    form.value.form_events = form.value.form_events.filter(c => c !== cfg);
  }
};

const configValue = (detected, key) => configFor(detected)?.[key] || '';

// Evento da Meta: lista de padrao + "Personalizado" (mesma escolha que existe
// na config do site). Um nome fora da lista JA salvo abre o campo livre
// sozinho; o conjunto abaixo so guarda a escolha enquanto o campo esta vazio.
const customMetaForms = ref(new Set());
const formKey = detected =>
  detected.form_id ? `id:${detected.form_id}` : `idx:${detected.index}`;

const isCustomMetaEvent = detected => {
  const value = configValue(detected, 'meta_event');
  if (value) return !META_STANDARD_EVENTS.includes(value);
  return customMetaForms.value.has(formKey(detected));
};

const onMetaEventSelect = (detected, value) => {
  const keys = new Set(customMetaForms.value);
  if (value === META_CUSTOM_OPTION) {
    keys.add(formKey(detected));
    setFormConfig(detected, 'meta_event', '');
  } else {
    keys.delete(formKey(detected));
    setFormConfig(detected, 'meta_event', value);
  }
  customMetaForms.value = keys;
};

const loadPage = async () => {
  if (isNew.value) return;
  isLoadingPage.value = true;
  try {
    const { data } = await LandingPagesAPI.getPage(site.value.id, pageId.value);
    pageMeta.value = data;
    form.value = {
      title: data.title || '',
      slug: data.slug,
      html: data.html || '',
      conversion_event: data.conversion_event || '',
      campaign_links: data.campaign_links || [],
      form_events: data.form_events || [],
      form_events_enabled: !!data.form_events_enabled,
    };
    previewHtml.value = form.value.html;
  } catch (error) {
    toast.error(apiError(error));
    backToList();
  } finally {
    isLoadingPage.value = false;
  }
};

const save = async ({ thenPublish = false } = {}) => {
  isSaving.value = true;
  try {
    const payload = { ...form.value };
    let saved = pageMeta.value;
    if (isNew.value) {
      const { data } = await LandingPagesAPI.createPage(site.value.id, payload);
      saved = data;
    } else {
      delete payload.slug;
      const { data } = await LandingPagesAPI.updatePage(
        site.value.id,
        pageId.value,
        payload
      );
      saved = data;
    }
    if (thenPublish) {
      await LandingPagesAPI.publishPage(site.value.id, saved.id);
      toast.success(t('LANDING_PAGES.PAGES.PUBLISH_QUEUED'));
    } else {
      toast.success(t('LANDING_PAGES.EDITOR.SAVED_DRAFT'));
    }
    backToList();
  } catch (error) {
    toast.error(apiError(error));
  } finally {
    isSaving.value = false;
  }
};

const openPreview = () => {
  if (pageMeta.value?.preview_url) {
    window.open(pageMeta.value.preview_url, '_blank', 'noopener');
  }
};

onMounted(async () => {
  await fetchSite();
  if (!site.value) {
    backToList();
    return;
  }
  await loadPage();
  if (!isNew.value) {
    activeTab.value = TABS.includes(route.query.tab)
      ? route.query.tab
      : 'results';
    loadInsights();
  }
});
</script>

<template>
  <SettingsPageLayout
    :title="
      isNew
        ? t('LANDING_PAGES.EDITOR.NEW_TITLE')
        : form.title || form.slug || t('LANDING_PAGES.EDITOR.EDIT_TITLE')
    "
    :description="isNew ? publicUrl.replace('https://', '') : ''"
    :kicker="t('LANDING_PAGES.EDITOR.KICKER')"
    :back-to="backRoute"
  >
    <!-- Endereco + chip de status (mesma linguagem da lista) -->
    <template v-if="!isNew && pageMeta" #description>
      <span class="font-mono text-[13px] text-n-slate-11">
        {{ publicUrl.replace('https://', '') }}
      </span>
      <span
        class="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ml-1.5 align-middle"
        :class="headerBadge.cls"
        :title="pageMeta.publish_error || ''"
      >
        {{ headerBadge.label }}
      </span>
    </template>
    <template #actions>
      <Button
        v-if="!isNew"
        :label="t('LANDING_PAGES.PAGES.PREVIEW')"
        icon="i-lucide-eye"
        variant="outline"
        color="slate"
        size="sm"
        @click="openPreview"
      />
      <Button
        :label="t('LANDING_PAGES.EDITOR.SAVE')"
        variant="outline"
        size="sm"
        :is-loading="isSaving"
        @click="save()"
      />
      <Button
        :label="t('LANDING_PAGES.EDITOR.SAVE_AND_PUBLISH')"
        size="sm"
        :is-loading="isSaving"
        @click="save({ thenPublish: true })"
      />
    </template>

    <div
      v-if="isLoadingSite || isLoadingPage"
      class="flex justify-center py-16"
    >
      <Spinner :size="28" class="text-n-brand" />
    </div>

    <template v-else>
      <!-- Abas -->
      <div class="flex gap-6 border-b border-n-weak mb-6">
        <button
          v-for="tab in TABS"
          :key="tab"
          class="pb-2.5 -mb-px text-[13px] font-medium border-b-2 transition-colors disabled:opacity-40"
          :class="
            activeTab === tab
              ? 'border-[#8B2520] dark:border-[#c4a882] text-n-slate-12'
              : 'border-transparent text-n-slate-10 hover:text-n-slate-11'
          "
          :disabled="(tab === 'links' || tab === 'results') && isNew"
          @click="activeTab = tab"
        >
          {{ t(`LANDING_PAGES.EDITOR.TAB_${tab.toUpperCase()}`) }}
        </button>
      </div>

      <!-- ═══ RESULTADOS ═══ -->
      <template v-if="activeTab === 'results'">
        <div class="flex justify-end mb-3">
          <span class="flex items-center gap-0.5 rounded-lg bg-n-alpha-1 p-0.5">
            <button
              v-for="periodOption in [7, 30, 90]"
              :key="periodOption"
              class="px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors tabular-nums"
              :class="
                insightsPeriod === periodOption
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-11'
              "
              @click="
                insightsPeriod = periodOption;
                loadInsights();
              "
            >
              {{ t('LANDING_PAGES.RESULTS.PERIOD_DAYS', { n: periodOption }) }}
            </button>
          </span>
        </div>
        <div v-if="isLoadingInsights" class="flex justify-center py-16">
          <Spinner :size="24" class="text-n-brand" />
        </div>
        <template v-else>
          <!-- KPIs (componente canonico dos relatorios) -->
          <div class="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <CrmKpiCard
              v-for="card in kpiCards"
              :key="card.key"
              :label="t(`LANDING_PAGES.RESULTS.KPI_${card.key.toUpperCase()}`)"
              :value="card.value"
              :subtitle="card.subtitle"
              :category="card.category"
              :show-delta="false"
            />
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
            <!-- Acessos (barras) × conversoes (linha) -->
            <SettingsSectionCard
              :title="t('LANDING_PAGES.RESULTS.CHART_TITLE')"
              class="!mb-0 flex flex-col"
            >
              <div
                v-if="!series.length"
                class="flex items-center justify-center h-28 text-[12.5px] text-n-slate-9 mt-auto"
              >
                {{ t('LANDING_PAGES.RESULTS.EMPTY') }}
              </div>
              <div v-else class="relative mt-auto">
                <div class="flex items-end gap-[2px] h-28">
                  <div
                    v-for="day in series"
                    :key="day.date"
                    class="flex-1 flex flex-col justify-end h-full"
                    @mousemove="
                      showTip(
                        $event,
                        `${fmtDay(day.date)}: ${day.views} ${t('LANDING_PAGES.RESULTS.LEGEND_VIEWS')} · ${day.conversions} ${t('LANDING_PAGES.RESULTS.LEGEND_CONVERSIONS')}`
                      )
                    "
                    @mouseleave="hideTip"
                  >
                    <div
                      class="w-full rounded-t bg-n-slate-5"
                      :style="{
                        height: `${Math.max((day.views / maxViews) * 100, day.views > 0 ? 3 : 1)}%`,
                      }"
                    />
                  </div>
                </div>
                <svg
                  class="absolute inset-0 w-full h-28 pointer-events-none overflow-visible"
                  :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
                  preserveAspectRatio="none"
                >
                  <polyline
                    :points="conversionPoints"
                    fill="none"
                    class="stroke-n-teal-9"
                    stroke-width="2"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div class="flex justify-between text-[10px] text-n-slate-9 mt-1">
                <span v-if="series.length">{{ fmtDay(series[0].date) }}</span>
                <span v-if="series.length">
                  {{ fmtDay(series[series.length - 1].date) }}
                </span>
              </div>
              <div class="flex gap-4 text-[11px] text-n-slate-10 mt-2">
                <span class="inline-flex items-center gap-1.5">
                  <i class="inline-block size-2 rounded-sm bg-n-slate-5" />
                  {{ t('LANDING_PAGES.RESULTS.LEGEND_VIEWS') }}
                </span>
                <span class="inline-flex items-center gap-1.5">
                  <i class="inline-block w-3 h-[3px] rounded bg-n-teal-9" />
                  {{ t('LANDING_PAGES.RESULTS.LEGEND_CONVERSIONS') }}
                </span>
              </div>
            </SettingsSectionCard>

            <!-- Origens dos ACESSOS (pageviews) -->
            <SettingsSectionCard
              :title="t('LANDING_PAGES.RESULTS.ACCESS_ORIGINS_TITLE')"
              :description="t('LANDING_PAGES.RESULTS.ACCESS_ORIGINS_SUB')"
              class="!mb-0"
            >
              <div
                v-if="!insights.access_origins.length"
                class="text-[13px] text-n-slate-10 py-3"
              >
                {{ t('LANDING_PAGES.RESULTS.ACCESS_ORIGINS_EMPTY') }}
              </div>
              <div v-else class="flex flex-col">
                <div
                  v-for="row in insights.access_origins"
                  :key="`ao-${row.source}-${row.medium}`"
                  class="flex items-center justify-between gap-3 py-2 border-b border-n-weak last:border-0 text-[12.5px]"
                >
                  <span class="font-mono text-n-slate-11 truncate">
                    {{ row.source
                    }}<template v-if="row.medium"> · {{ row.medium }}</template>
                  </span>
                  <span class="flex items-center gap-2 shrink-0">
                    <span
                      class="inline-block w-24 h-1.5 rounded-full bg-n-slate-3 overflow-hidden"
                    >
                      <span
                        class="block h-full rounded-full bg-n-teal-9"
                        :style="{ width: `${row.share}%` }"
                      />
                    </span>
                    <b class="tabular-nums w-8 text-right">{{ row.count }}</b>
                  </span>
                </div>
              </div>
            </SettingsSectionCard>

            <!-- Origem × meio -->
            <SettingsSectionCard
              :title="t('LANDING_PAGES.RESULTS.SOURCES_TITLE')"
              :description="t('LANDING_PAGES.RESULTS.SOURCES_SUB')"
              class="!mb-0"
            >
              <div
                v-if="!insights.sources.length"
                class="text-[13px] text-n-slate-10 py-3"
              >
                {{ t('LANDING_PAGES.RESULTS.EMPTY') }}
              </div>
              <div v-else class="flex flex-col">
                <div
                  v-for="row in insights.sources"
                  :key="`${row.source}-${row.medium}`"
                  class="flex items-center justify-between gap-3 py-2 border-b border-n-weak last:border-0 text-[12.5px]"
                >
                  <span class="font-mono text-n-slate-11 truncate">
                    {{ row.source
                    }}<template v-if="row.medium"> · {{ row.medium }}</template>
                  </span>
                  <span class="flex items-center gap-2 shrink-0">
                    <span
                      class="inline-block w-24 h-1.5 rounded-full bg-n-slate-3 overflow-hidden"
                    >
                      <span
                        class="block h-full rounded-full bg-n-blue-9"
                        :style="{ width: `${row.share}%` }"
                      />
                    </span>
                    <b class="tabular-nums w-8 text-right">{{ row.count }}</b>
                  </span>
                </div>
              </div>
            </SettingsSectionCard>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch mt-5">
            <!-- Por link de campanha -->
            <SettingsSectionCard
              :title="t('LANDING_PAGES.RESULTS.LINKS_TITLE')"
              :description="t('LANDING_PAGES.RESULTS.LINKS_SUB')"
              class="!mb-0"
            >
              <div
                v-if="!insights.links.length"
                class="text-[13px] text-n-slate-10 py-3"
              >
                {{ t('LANDING_PAGES.RESULTS.LINKS_EMPTY') }}
              </div>
              <div v-else class="flex flex-col">
                <div
                  v-for="link in insights.links"
                  :key="link.name"
                  class="flex items-center justify-between gap-3 py-2 border-b border-n-weak last:border-0 text-[12.5px]"
                >
                  <span class="text-n-slate-11 truncate">{{ link.name }}</span>
                  <b class="tabular-nums shrink-0">{{ link.conversions }}</b>
                </div>
              </div>
            </SettingsSectionCard>

            <!-- Ultimos leads -->
            <SettingsSectionCard
              :title="t('LANDING_PAGES.RESULTS.RECENT_TITLE')"
              :description="t('LANDING_PAGES.RESULTS.RECENT_SUB')"
              class="!mb-0"
            >
              <div
                v-if="!insights.recent.length"
                class="text-[13px] text-n-slate-10 py-3"
              >
                {{ t('LANDING_PAGES.RESULTS.EMPTY') }}
              </div>
              <div v-else class="overflow-x-auto">
                <table class="w-full">
                  <thead>
                    <tr class="border-b border-n-weak">
                      <th
                        class="py-2 text-left text-[10px] font-semibold text-n-slate-9 uppercase tracking-[0.07em]"
                      >
                        {{ t('LANDING_PAGES.RESULTS.RECENT_COL_LEAD') }}
                      </th>
                      <th
                        class="py-2 pl-3 text-left text-[10px] font-semibold text-n-slate-9 uppercase tracking-[0.07em]"
                      >
                        {{ t('LANDING_PAGES.RESULTS.RECENT_COL_ORIGIN') }}
                      </th>
                      <th
                        class="py-2 pl-3 text-right text-[10px] font-semibold text-n-slate-9 uppercase tracking-[0.07em]"
                      >
                        {{ t('LANDING_PAGES.RESULTS.RECENT_COL_WHEN') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="lead in insights.recent"
                      :key="lead.occurred_at + lead.contact_name"
                      class="border-b border-n-weak/60 last:border-0 transition-colors"
                      :class="
                        lead.contact_id
                          ? 'cursor-pointer hover:bg-n-solid-3'
                          : ''
                      "
                      @click="goToContact(lead)"
                    >
                      <td
                        class="py-2 pr-3 text-[12.5px] font-medium text-n-slate-12"
                      >
                        {{ lead.contact_name }}
                      </td>
                      <td
                        class="py-2 pl-3 font-mono text-[11px] text-n-slate-10"
                      >
                        {{ lead.source
                        }}<template v-if="lead.medium">
                          · {{ lead.medium }}
                        </template>
                      </td>
                      <td
                        class="py-2 pl-3 text-right text-[11.5px] text-n-slate-9 tabular-nums whitespace-nowrap"
                      >
                        {{ fmtDateTime(lead.occurred_at) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </SettingsSectionCard>
          </div>
        </template>
      </template>

      <!-- ═══ CONTEUDO ═══ -->
      <template v-else-if="activeTab === 'content'">
        <SettingsSectionCard>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <Input
              v-model="form.title"
              :label="t('LANDING_PAGES.EDITOR.PAGE_TITLE')"
            />
            <Input
              v-model="form.slug"
              :label="t('LANDING_PAGES.EDITOR.SLUG')"
              :placeholder="t('LANDING_PAGES.EDITOR.SLUG_PLACEHOLDER')"
              :disabled="!isNew"
            />
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Codigo -->
            <div class="flex flex-col">
              <span class="mb-1.5 text-sm font-medium text-n-slate-12">
                {{ t('LANDING_PAGES.EDITOR.HTML') }}
              </span>
              <textarea
                v-model="form.html"
                spellcheck="false"
                class="!mb-0 !h-[540px] w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-n-slate-12 resize-none"
                :placeholder="t('LANDING_PAGES.EDITOR.HTML_PLACEHOLDER')"
              />
            </div>

            <!-- Previa em moldura de dispositivo -->
            <div class="flex flex-col">
              <div class="flex items-center justify-between mb-1.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('LANDING_PAGES.EDITOR.LIVE_PREVIEW') }}
                </span>
                <span
                  class="flex items-center gap-0.5 rounded-lg bg-n-alpha-1 p-0.5"
                >
                  <button
                    v-for="deviceKey in ['desktop', 'mobile']"
                    :key="deviceKey"
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-medium transition-colors"
                    :class="
                      previewDevice === deviceKey
                        ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                        : 'text-n-slate-10 hover:text-n-slate-11'
                    "
                    @click="previewDevice = deviceKey"
                  >
                    <span
                      class="size-3.5"
                      :class="
                        deviceKey === 'mobile'
                          ? 'i-lucide-smartphone'
                          : 'i-lucide-monitor'
                      "
                    />
                    {{
                      t(
                        `LANDING_PAGES.EDITOR.DEVICE_${deviceKey.toUpperCase()}`
                      )
                    }}
                  </button>
                </span>
              </div>
              <div
                ref="previewStage"
                class="h-[540px] rounded-lg bg-n-slate-3 flex items-center justify-center overflow-hidden"
              >
                <div
                  class="rounded-md overflow-hidden shadow-lg ring-1 ring-n-slate-6 transition-[width,height] duration-200"
                  :style="screenStyle"
                >
                  <iframe
                    :srcdoc="previewHtml"
                    sandbox="allow-scripts"
                    :title="t('LANDING_PAGES.EDITOR.LIVE_PREVIEW')"
                    class="bg-white origin-top-left"
                    :style="frameStyle"
                  />
                </div>
              </div>
              <p class="text-[11px] text-n-slate-9 mt-1.5 mb-0 text-right">
                {{
                  t('LANDING_PAGES.EDITOR.PREVIEW_SCALE', {
                    width: device.width,
                    height: device.height,
                    scale: Math.round(previewScale * 100),
                  })
                }}
              </p>
            </div>
          </div>

          <p class="text-[11.5px] text-n-slate-9 mt-4 mb-0">
            {{ t('LANDING_PAGES.EDITOR.TRACKING_NOTE') }}
          </p>

          <!-- Evento de conversao (avancado; raro precisar mexer) -->
          <div class="mt-5 pt-4 border-t border-n-weak max-w-[26rem]">
            <Input
              v-model="form.conversion_event"
              :label="t('LANDING_PAGES.EDITOR.EVENT')"
              :placeholder="pageMeta?.conversion_event || ''"
              :message="t('LANDING_PAGES.EDITOR.EVENT_HINT')"
            />
          </div>
        </SettingsSectionCard>
      </template>

      <!-- ═══ AVANCADO — EVENTOS POR FORMULARIO ═══ -->
      <template v-else-if="activeTab === 'advanced'">
        <SettingsSectionCard
          :title="t('LANDING_PAGES.FORM_EVENTS.TITLE_TAB')"
          :description="t('LANDING_PAGES.FORM_EVENTS.TAB_SUB')"
        >
          <div class="flex items-center gap-3 mb-1">
            <Switch v-model="form.form_events_enabled" />
            <span class="text-[13px] text-n-slate-12 font-medium">
              {{ t('LANDING_PAGES.FORM_EVENTS.ENABLE') }}
            </span>
          </div>
          <p class="text-[12px] text-n-slate-9 mb-0">
            {{ t('LANDING_PAGES.FORM_EVENTS.ENABLE_HINT') }}
          </p>
          <template v-if="form.form_events_enabled">
            <div
              class="flex items-start gap-2 mt-3 px-3 py-2.5 rounded-lg bg-n-alpha-1 text-[12px] text-n-slate-11 max-w-[52rem]"
            >
              <span
                class="i-lucide-info size-4 shrink-0 mt-0.5 text-n-slate-9"
              />
              <span>{{ t('LANDING_PAGES.FORM_EVENTS.EXPLAINER') }}</span>
            </div>

            <p
              v-if="!detectedForms.length"
              class="text-[12.5px] text-n-slate-9 mt-3 mb-0"
            >
              {{ t('LANDING_PAGES.FORM_EVENTS.NONE') }}
            </p>

            <div
              v-for="detected in detectedForms"
              :key="`${detected.form_id || 'idx'}-${detected.index}`"
              class="mt-3 rounded-xl border border-n-weak px-4 py-3 max-w-[52rem]"
            >
              <div class="flex items-baseline gap-2 flex-wrap">
                <b class="text-[13px] text-n-slate-12">
                  {{
                    t('LANDING_PAGES.FORM_EVENTS.FORM_N', {
                      n: detected.index + 1,
                    })
                  }}
                </b>
                <span
                  v-if="detected.form_id"
                  class="font-mono text-[11px] text-n-teal-10"
                >
                  {{ `id="${detected.form_id}"` }}
                </span>
                <span class="text-[11.5px] text-n-slate-9">
                  <template v-if="detected.button">
                    {{
                      t('LANDING_PAGES.FORM_EVENTS.BUTTON', {
                        label: detected.button,
                      })
                    }}
                  </template>
                  <template v-if="detected.fields.length">
                    · {{ detected.fields.join(', ') }}
                  </template>
                </span>
              </div>

              <div
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3"
              >
                <label class="flex flex-col gap-1">
                  <span class="text-[12px] font-medium text-n-slate-11">
                    {{ t('LANDING_PAGES.FORM_EVENTS.MATCH_BY') }}
                  </span>
                  <select
                    :value="configValue(detected, 'match_by') || 'index'"
                    class="!mb-0 !h-9 rounded-lg text-[13px]"
                    @change="
                      setFormConfig(detected, 'match_by', $event.target.value)
                    "
                  >
                    <option value="index">
                      {{ t('LANDING_PAGES.FORM_EVENTS.MATCH_INDEX') }}
                    </option>
                    <option value="id" :disabled="!detected.form_id">
                      {{ t('LANDING_PAGES.FORM_EVENTS.MATCH_ID') }}
                    </option>
                  </select>
                </label>
                <label class="flex flex-col gap-1">
                  <span class="text-[12px] font-medium text-n-slate-11">
                    {{ t('LANDING_PAGES.FORM_EVENTS.CRM_EVENT') }}
                  </span>
                  <input
                    :value="configValue(detected, 'crm_event')"
                    :placeholder="
                      form.conversion_event || pageMeta?.conversion_event || ''
                    "
                    class="!mb-0 !h-9 rounded-lg text-[13px]"
                    @input="
                      setFormConfig(detected, 'crm_event', $event.target.value)
                    "
                  />
                </label>
                <label class="flex flex-col gap-1">
                  <span class="text-[12px] font-medium text-n-slate-11">
                    {{ t('LANDING_PAGES.FORM_EVENTS.SOURCE') }}
                  </span>
                  <input
                    :value="configValue(detected, 'lead_source')"
                    :placeholder="t('LANDING_PAGES.FORM_EVENTS.SOURCE_PH')"
                    class="!mb-0 !h-9 rounded-lg text-[13px]"
                    @input="
                      setFormConfig(
                        detected,
                        'lead_source',
                        $event.target.value
                      )
                    "
                  />
                </label>
                <label class="flex flex-col gap-1">
                  <span class="text-[12px] font-medium text-n-slate-11">
                    {{ t('LANDING_PAGES.FORM_EVENTS.META_EVENT') }}
                  </span>
                  <select
                    :value="
                      isCustomMetaEvent(detected)
                        ? META_CUSTOM_OPTION
                        : configValue(detected, 'meta_event')
                    "
                    class="!mb-0 !h-9 rounded-lg text-[13px]"
                    @change="onMetaEventSelect(detected, $event.target.value)"
                  >
                    <option value="">
                      {{ t('LANDING_PAGES.FORM_EVENTS.META_DEFAULT') }}
                    </option>
                    <option
                      v-for="eventName in META_STANDARD_EVENTS"
                      :key="eventName"
                      :value="eventName"
                    >
                      {{ eventName }}
                    </option>
                    <option :value="META_CUSTOM_OPTION">
                      {{ t('LANDING_PAGES.CAPI.EVENT_CUSTOM') }}
                    </option>
                  </select>
                  <input
                    v-if="isCustomMetaEvent(detected)"
                    :value="configValue(detected, 'meta_event')"
                    :placeholder="t('LANDING_PAGES.FORM_EVENTS.META_CUSTOM_PH')"
                    class="!mb-0 !h-9 rounded-lg text-[13px]"
                    @input="
                      setFormConfig(detected, 'meta_event', $event.target.value)
                    "
                  />
                </label>
              </div>
            </div>

            <p class="text-[11px] text-n-slate-9 mt-3 mb-0 max-w-[52rem]">
              {{ t('LANDING_PAGES.FORM_EVENTS.PUBLISH_NOTE') }}
            </p>
          </template>
        </SettingsSectionCard>
      </template>

      <!-- ═══ LINKS DE CAMPANHA ═══ -->
      <template v-else-if="activeTab === 'links'">
        <div class="max-w-[52rem]">
          <div v-if="form.campaign_links.length" class="mb-6">
            <SettingsTable :headers="linkHeaders">
              <tr
                v-for="(link, index) in form.campaign_links"
                :key="index"
                class="group hover:bg-n-solid-2 transition-colors"
              >
                <td class="px-5 py-3">
                  <span class="text-[13px] font-semibold text-n-slate-12">
                    {{ link.name }}
                  </span>
                </td>
                <td
                  class="px-5 py-3 font-mono text-[12px] text-n-slate-11 whitespace-nowrap"
                >
                  {{ link.utm_source
                  }}<template v-if="link.utm_medium">
                    · {{ link.utm_medium }}
                  </template>
                </td>
                <td class="px-5 py-3 font-mono text-[12px] text-n-slate-11">
                  {{ link.utm_campaign || '—' }}
                </td>
                <td class="px-5 py-3 text-right tabular-nums text-sm">
                  <b
                    v-if="linkConversions[link.name] != null"
                    class="text-n-slate-12"
                  >
                    {{ linkConversions[link.name] }}
                  </b>
                  <span v-else class="text-n-slate-9">—</span>
                </td>
                <td class="px-5 py-3">
                  <span
                    class="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Button
                      :label="t('LANDING_PAGES.UTM.COPY_SHORT')"
                      icon="i-lucide-copy"
                      size="xs"
                      variant="ghost"
                      color="slate"
                      :title="link.url"
                      @click="copyLink(link.url)"
                    />
                    <Button
                      :label="t('LANDING_PAGES.UTM.DELETE')"
                      size="xs"
                      variant="ghost"
                      color="ruby"
                      @click="removeLink(index)"
                    />
                  </span>
                </td>
              </tr>
            </SettingsTable>
          </div>
          <p v-else class="text-[13px] text-n-slate-10 mb-6">
            {{ t('LANDING_PAGES.UTM.EMPTY') }}
          </p>

          <SettingsSectionCard
            :title="t('LANDING_PAGES.UTM.NEW')"
            :description="t('LANDING_PAGES.UTM.DESCRIPTION')"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Input
                v-model="linkForm.name"
                :label="t('LANDING_PAGES.UTM.NAME')"
                :placeholder="t('LANDING_PAGES.UTM.NAME_PLACEHOLDER')"
              />
              <label class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('LANDING_PAGES.UTM.TYPE') }}
                </span>
                <select
                  v-model="linkForm.type"
                  class="w-full !mb-0 !h-10 rounded-lg"
                >
                  <option
                    v-for="type in LANDING_PAGE_TRAFFIC_TYPES"
                    :key="type.key"
                    :value="type.key"
                  >
                    {{ t(`LANDING_PAGES.UTM.TYPES.${type.key}`) }}
                  </option>
                </select>
              </label>
            </div>

            <!-- Como entra nos relatorios: origem/meio derivados do tipo,
                 visiveis e travados (editaveis so no personalizado) -->
            <div class="rounded-lg border border-n-weak bg-n-slate-2 p-3 mb-4">
              <div
                class="text-[11px] font-medium text-n-slate-10 uppercase tracking-wide mb-2"
              >
                {{ t('LANDING_PAGES.UTM.PREVIEW_TITLE') }}
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <span class="block text-[11px] text-n-slate-9 mb-0.5">
                    {{ t('LANDING_PAGES.UTM.SOURCE') }}
                  </span>
                  <Input
                    v-if="isCustomType"
                    v-model="linkForm.custom_source"
                    size="sm"
                  />
                  <span
                    v-else
                    class="inline-flex items-center gap-1 font-mono text-[13px] font-medium text-n-slate-12"
                  >
                    <span class="i-lucide-lock size-3 text-n-slate-8" />
                    {{ activeType.source }}
                  </span>
                </div>
                <div>
                  <span class="block text-[11px] text-n-slate-9 mb-0.5">
                    {{ t('LANDING_PAGES.UTM.MEDIUM') }}
                  </span>
                  <Input
                    v-if="isCustomType"
                    v-model="linkForm.custom_medium"
                    size="sm"
                  />
                  <span
                    v-else
                    class="inline-flex items-center gap-1 font-mono text-[13px] font-medium text-n-slate-12"
                  >
                    <span class="i-lucide-lock size-3 text-n-slate-8" />
                    {{ activeType.medium }}
                  </span>
                </div>
              </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <Input
                v-model="linkForm.utm_campaign"
                :label="t('LANDING_PAGES.UTM.CAMPAIGN')"
                :placeholder="t('LANDING_PAGES.UTM.CAMPAIGN_PLACEHOLDER')"
              />
              <Input
                v-model="linkForm.utm_content"
                :label="t('LANDING_PAGES.UTM.CONTENT')"
                :placeholder="t('LANDING_PAGES.UTM.OPTIONAL')"
              />
            </div>
            <code
              class="block font-mono text-[11.5px] text-n-slate-11 break-all mb-4"
            >
              {{ linkPreview }}
            </code>
            <Button
              :label="t('LANDING_PAGES.UTM.SAVE_LINK')"
              size="sm"
              :disabled="!canAddLink"
              @click="addLink"
            />
          </SettingsSectionCard>
          <p class="text-[11.5px] text-n-slate-9 mt-3 mb-0">
            {{ t('LANDING_PAGES.UTM.NOTE') }}
          </p>
        </div>
      </template>
    </template>
    <div
      v-if="tip"
      class="fixed z-50 px-2.5 py-1.5 rounded-lg bg-n-solid-3 outline outline-1 outline-n-container shadow-lg text-[11.5px] text-n-slate-12 pointer-events-none max-w-[240px]"
      :style="tipStyle(tip)"
    >
      {{ tip.text }}
    </div>
  </SettingsPageLayout>
</template>
